import React, { useCallback, useState, useEffect, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, Paper } from "@material-ui/core";

import palette from "../../styles/palette";
import RightDrawerModal from "../../RightDrawerModal";
import ModalTable from "../../common/Table/ModalTable";
import {
  DISMISS,
  UNABLE_TRANSFER_TO_LOCKED_ROUTE,
  MOVE_AND_SCHEDULE,
  MOVE_AND_OPTIMISE,
  MOVE_ONLY,
  REMOVE,
  REMOVE_OPTIONS,
  AFFECTED_STOPS,
  AVAILABLE_ROUTES,
  SELECT_PLACEHOLDER,
  TRANSFER_HIGH_VALUE_PARCEL,
  NO_PARCELS_MATCH_CRITERIA,
} from "../../../constants/strings";
import SelectDriverRoute from "../DriverRequests/components/SelectDriverRoute";
import useLocationQueryParams from "../../../hooks/useLocationQueryParams";
import { getCurrentUserId } from "../../../store/selectors/user";
import { getAvailableRoutesTransfer } from "../../../store/actions/request";
import {
  headerCellStyles,
  bodyCellStyles,
} from "../../../features/request/tableStyles";

import { showNotification } from "../../../store/actions/notifications";
import { NotificationType } from "../../../features/Notifier";
import { isRouteReadyOrOpen } from "../../../store/selectors/route";

import { useOverlay } from "../../Overlay/useOverlay";

import { isRouteLocked } from "../TransferTo/utils";
import SliderCardAutocomplete from "../../SliderCardAutocomplete";
import { REMOVE_OPTIONS_LIST } from "./removeOptions";
import TransferActionButtons from "../TransferTo/components/TransferActionButtons";
import { getUniqueRoutesFromSelectedStops } from "../../../models/routeDetail";
import affectedStopsTableProps from "./affectedStopsTableProps";
import { hasHighValueParcel } from "../../../models/parcel";
import StaticBanner from "../../StaticBanner";
import { Text } from "../../Base";

const useStyles = makeStyles(({ typography }) => ({
  root: {
    margin: "40px 125px",
    borderRadius: 10,
  },
  message: {
    padding: "15px 25px",
    color: palette.typography.black.high,
    fontSize: typography.pxToRem(16),
    fontWeight: 500,
    lineHeight: typography.pxToRem(20),
    letterSpacing: typography.pxToRem(-0.32),
    textTransform: "capitalize",
  },
}));

const RemoveParcels = ({
  open,
  onClose,
  onConfirm,
  onMove,
  getAffectedStops,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { showOverlayWhile } = useOverlay();

  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedRemoveOption, setRemoveOption] = useState(null);
  const [affectedStops, setAffectedStops] = useState([]);
  const [routes, setRoutes] = useState([]);

  const query = useLocationQueryParams();
  const uid = useSelector(getCurrentUserId);
  const routeReadyOrOpen = useSelector(isRouteReadyOrOpen);
  const { depot, date } = query;

  const isActionBtnDisabled =
    !selectedRoute ||
    isRouteLocked(routes, selectedRoute) ||
    !affectedStops.length;

  const getStops = useCallback(
    async (selectedRemoveOption) => {
      if (selectedRemoveOption) {
        const processedStops = await getAffectedStops(selectedRemoveOption);
        return processedStops;
      }
      return [];
    },
    [getAffectedStops]
  );

  useEffect(() => {
    const fetchRoutes = async () => {
      const availableRoutes = await dispatch(
        getAvailableRoutesTransfer(
          depot,
          date,
          uid,
          getUniqueRoutesFromSelectedStops(affectedStops)
        )
      );
      setRoutes(availableRoutes);
    };
    selectedRemoveOption && fetchRoutes();
  }, [date, depot, uid, selectedRemoveOption, affectedStops, dispatch]);

  const onMoveParcels = useCallback(
    async (optimise, transferOnly) => {
      // TODO add action ID
      const success = await onMove(selectedRoute, selectedRemoveOption);
      success && onConfirm({ optimise, transferOnly });
    },
    [selectedRoute, selectedRemoveOption, onMove, onConfirm]
  );

  const getTableProps = useCallback(
    () =>
      affectedStopsTableProps(
        headerCellStyles(),
        bodyCellStyles(),
        affectedStops
      ),
    [affectedStops]
  );

  const tableProps = getTableProps();

  const showHighValueMessage = useMemo(
    () => hasHighValueParcel(tableProps.data),
    [tableProps.data]
  );

  const onChange = useCallback(
    (value) => {
      if (isRouteLocked(routes, value)) {
        dispatch(
          showNotification({
            key: "transfer-locked-route-error",
            type: NotificationType.ACTION,
            message: UNABLE_TRANSFER_TO_LOCKED_ROUTE,
            buttonText: DISMISS,
          })
        );
      }

      setSelectedRoute(value);
      // TODO add action id
    },
    [routes, dispatch]
  );

  const onRemoveOptionsChange = useCallback(
    async (value) => {
      const removeOption =
        value && REMOVE_OPTIONS_LIST.find((option) => option.value === value);
      const stops = await getStops(removeOption);
      setAffectedStops(stops);
      setRemoveOption(removeOption);
      setSelectedRoute(null);
      // TODO add action id
    },
    [getStops]
  );

  const processedMoveAction = useCallback(
    (optimise, transferOnly) =>
      showOverlayWhile(async () => await onMoveParcels(optimise, transferOnly)),
    [onMoveParcels, showOverlayWhile]
  );

  const removeButtonsConfig = useMemo(
    () => ({
      move: {
        isDisabled: !routeReadyOrOpen || isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.orange,
          border: palette.modalButtons.greenBorder,
        },
        title: MOVE_ONLY,
        onClick: processedMoveAction(false, true),
      },
      optimise: {
        isDisabled: !routeReadyOrOpen || isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.green,
          border: palette.modalButtons.greenBorder,
        },
        title: MOVE_AND_OPTIMISE,
        onClick: processedMoveAction(true, false),
      },
      schedule: {
        isDisabled: isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.blue,
          border: palette.btn.blueBorder,
        },
        title: MOVE_AND_SCHEDULE,
        onClick: processedMoveAction(false, false),
      },
    }),
    [routeReadyOrOpen, isActionBtnDisabled, processedMoveAction]
  );

  return (
    <RightDrawerModal
      open={open}
      onClose={onClose}
      title={REMOVE}
      actions={<TransferActionButtons config={removeButtonsConfig} />}
    >
      {showHighValueMessage && (
        <StaticBanner message={TRANSFER_HIGH_VALUE_PARCEL} />
      )}
      <SliderCardAutocomplete
        options={REMOVE_OPTIONS_LIST}
        onChange={onRemoveOptionsChange}
        value={selectedRemoveOption?.value || null}
        sliderTitle={REMOVE_OPTIONS}
        inputLabel={REMOVE_OPTIONS}
      />
      {selectedRemoveOption && !affectedStops.length && (
        <Paper className={classes.root} elevation={5}>
          <Text className={classes.message} component="h6">
            {NO_PARCELS_MATCH_CRITERIA}
          </Text>
        </Paper>
      )}
      {selectedRemoveOption && !!affectedStops.length && (
        <>
          <ModalTable title={AFFECTED_STOPS} {...tableProps} />
          <SelectDriverRoute
            options={routes}
            onChange={onChange}
            value={selectedRoute}
            title={AVAILABLE_ROUTES}
            label={null}
            placeholder={SELECT_PLACEHOLDER}
          />
        </>
      )}
    </RightDrawerModal>
  );
};

export default memo(RemoveParcels);
